/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:variable-name

import { EntityVersion, ID, WithDirtyType, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock } from '@gh/core-data';
import { FormulaDto } from '../common/formula.dto';
import { CSMasterListItemStatus } from '../cs-master/models/cs-master.model';
import { SpecTemplateStatus } from '../job-spec-templates/job-spec-template.dto';

export const PriceBookUsage = createEnum('PRICE_BOOK_USAGE', {
  Normal: 'NORMAL',
  ChangeOrders: 'CHANGE_ORDERS',
});
export const PriceBookUsageWithWeb = createEnum('PRICE_BOOK_USAGE_WITH_WEB', {
  Normal: 'NORMAL',
  ChangeOrders: 'CHANGE_ORDERS',
  Web: 'WEB',
});

export type PriceBookUsage = EnumSet<typeof PriceBookUsage>;
export type PriceBookUsageWithWeb = EnumSet<typeof PriceBookUsageWithWeb>;

export const PbItemComponentFinderView = createEnum('FIND_ITEM_COMPONENT_VIEW', {
  ByCostCenter: 'BY_COST_CENTER',
  FullList: 'FULL_LIST',
});

export type PbItemComponentFinderView = EnumSet<typeof PbItemComponentFinderView>;

export type PriceBookFilterDto = {
  searchString?: string;
  usage?: PriceBookUsage[];
  showExpired?: boolean;
  showUnreleased?: boolean;
  autocomplete?: boolean;
  showActive?: boolean;
};

export type PriceBookShortDto = {
  priceBookId: ID;
  name: string;
};

export type PriceBookListItemDto = WithVersion<{
  priceBookId: ID;
  name: string;
  releaseDate: Date;
  withdrawalDate: Date;
  repriceDate: Date;
  usage: PriceBookUsageWithWeb;
}>;

export type PbCostCenterDto = WithDirtyType<{
  costCenterId: ID;
  code: number;
  name: string;
  isEmpty: boolean;
  items?: PbCcElementDto[];
  preloaded?: boolean;
}>;

export type PbDefaultsDto = {
  deletePct: number;
  itemRoundUp: number;
  isQqAllowed: boolean;
  qqMarkupPct: number;
  priceRoundUp: number;
  itemMarkupPct: number;
  profitMarkupPct: number;
  componentRoundUp: number;
  overheadMarkupPct: number;
  componentMarkupPct: number;
  contingencyMarkupPct: number;
};

export type PbDetailsDto = WithVersion<WithOptimisticLock<{
  priceBookId: Maybe<ID>;
  name: string;
  memo: string;
  defaults: PbDefaultsDto;
  costCenters: PbCostCenterDto[];
  releaseDate: Date;
  withdrawalDate: Date;
  housePlanIds: ID[];
  repriceDate?: Date;
  housePlans?: PbHousePlanDto[];
  usage?: PriceBookUsage;
}>>;

export type PbDetailsSaveDto = WithVersion<WithOptimisticLock<{
  name: string;
  memo: string;
  usage: PriceBookUsage;
  defaults: PbDefaultsDto;
  costCenters: PbCostCenterSaveDto[];
  repriceDate: Date;
  releaseDate: Date;
  withdrawalDate: Date;
  housePlans: PbHousePlanSaveDto[];
  items: PbCcStructureSaveDto[];
  isPersist: boolean;
  priceBookId?: ID;
}>>;

export type PbDetailsSaveResponseDto = WithVersion<WithOptimisticLock<{
  priceBookId: Maybe<ID>;
  housePlans: PbHousePlanDto[];
  items: PbCcElementDto[];
}>>;

export type PbHousePlanDto = WithDirtyType<{
  rn: number;
  housePlanId: ID;
  code: string;
  isQq: boolean;
  name: string;
  cost: number;
  profit: number;
  isManual: boolean;
  totalPct: number;
  calcPrice: number;
  costMeter: number;
  floorArea: number;
  salesPrice: number;
  trueProfit: number;
  isQqAllowed: boolean;
  qqMarkupPct: number;
  priceRoundUp: number;
  profitMarkupPct: number;
  salesPriceMeter: number;
  calcPriceWithGst: number;
  overheadMarkupPct: number;
  contingencyMarkupPct: number;
  salesPriceWithoutGst: number;
}>;

export type PbCcElementJstRefDto = { specTemplateId: ID; specTemplateParagraphId: ID; specTemplateSectionId?: ID };
export type PbCcElementCsmRefDto = { csMasterId: ID; csMasterSubHeadingId: ID; csMasterItemId?: ID };

export type PbCcElementDto = WithDirtyType<{
  itemId: ID;
  code: string;
  cost: number;
  name: string;
  price: number;
  roundUp: number;
  uomCode: string;
  isActive: boolean;
  deletePct: number;
  markupPct: number;
  isSalesTax: boolean;
  isComponent: boolean;
  priceWithGst?: number;
  costCenterId: ID;
  memo: string;
  jobSpecLayout?: PbCcElementJstRefDto[];
  jobCsLayout?: PbCcElementCsmRefDto[];
}>;

export type PbHousePlansRequestDto = WithVersion<WithOptimisticLock<{
  housePlans: PbHousePlanSaveDto[];
  isSalesEstimate?: boolean;
  isQuickQuote?: boolean;
  allowQq?: boolean;
  searchString?: string;
  priceBookId?: ID;
}>>;

export type PbHousePlanSaveDto = WithDirtyType<{
  housePlanId: ID;
  createdDate: Date;
  cost: number;
  isQqAllowed: boolean;
  isManual: boolean;
  floorArea: number;
  qqMarkupPct: number;
  priceRoundUp: number;
  profitMarkupPct: number;
  overheadMarkupPct: number;
  contingencyMarkupPct: number;
  salesPriceManual?: number;
}>;

export type PbCostCenterSaveDto = WithDirtyType<{
  costCenterId: ID;
}>;

export type PbCcStructureSaveDto = WithDirtyType<{
  itemId: ID;
  cost: number;
  price: number;
  roundUp: number;
  code: string;
  deletePct: number;
  markupPct: number;
  isSalesTax: boolean;
  isComponent: boolean;
  costCenterId: ID;
}>;

export type PbHousePlanCalculateResponseDto = {
  profit: number;
  totalPct: number;
  calcPrice: number;
  costMeter: number;
  salesPrice: number;
  trueProfit: number;
  salesPriceMeter: number;
  calcPriceWithGst: number;
};

export type PbCostCenterRequestDto = WithOptimisticLock<{
  priceBookId: ID;
}>;

export type PbGetCostCenterRequestStructureDto = WithDirtyType<{
  stuctureId: ID;
  priceBookId: ID;
  quantity: number;
  structure: PbCcElementDto;
  formula: FormulaDto;
  Active: boolean;
}>;

export type PbGetCostCenterRequestDto = WithVersion<{
  priceBookId: ID;
  items?: PbCcStructureSaveDto[];
  costCenterId?: ID;
}>;

export type PbCalculatedRequestDto = { housePlanId?: ID; jobId?: ID };
export type PbCalculatedCcDto = { costCenterId: ID; code: number; name: string; items: PbCalculatedStructureDto[] };
export type PbCalculatedStructureDto = PbCcElementDto & { origQuantity: number };

export type PbLinkCostCenterDto = {
  id: ID;
  code: number;
  description: string;
  version: EntityVersion;
};

export type PbLinkLevel = { id: ID; name: string };

export type PbLinkJstSectionDto = PbLinkLevel;

export type PbLinkJstParagraphDto = PbLinkLevel & { sections: PbLinkJstSectionDto[] };

export type PbLinkJstDto = PbLinkLevel & {
  status: SpecTemplateStatus;
  paragraphs: PbLinkJstParagraphDto[];
};

export type PbLinkJstGetResponseDto = {
  priceBookId: ID;
  priceBookName: string;
  costCenters: PbLinkCostCenterDto[];
  specTemplates: PbLinkJstDto[];
};

export type PbLinkCcElementSaveDto = {
  itemId: ID;
  costCenterId: ID;
  costCenterCode: number;
  itemCode: string;
  jobSpecLayout?: PbCcElementJstRefDto[];
  jobCsLayout?: PbCcElementCsmRefDto[];
};
export type PbLinkSaveRequestDto = PbLinkCcElementSaveDto[];
export type PbLinkSaveResponseDto = Hash<string[]>;

export type PbLinkCsmItemDto = PbLinkLevel;

export type PbLinkCsmSubheadingDto = PbLinkLevel & { items: PbLinkCsmItemDto[] };

export type PbLinkCsmHeadingDto = PbLinkLevel & { subheadings: PbLinkCsmSubheadingDto[] };

export type PbLinkCsmDto = PbLinkLevel & {
  status: CSMasterListItemStatus;
  headings: PbLinkCsmHeadingDto[];
};

export type PbLinkCsmGetResponseDto = {
  priceBookId: ID;
  priceBookName: string;
  costCenters: PbLinkCostCenterDto[];
  colorSelections: PbLinkCsmDto[];
};
